import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, AlertTriangle, CheckCircle2, Lock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { payoutsAPI, formatCents, type PayoutQueue, type QueuedRun } from '../api';

/**
 * The payment queue — read a clear statement of how a number was reached, then
 * approve it.
 *
 * Two things this deliberately does NOT do:
 *  - it never computes an amount. Every figure is server-computed from stored
 *    terms and re-derived at approval time; the browser only sends an intent.
 *  - approving does not move money. It records the run as `marked`. Queueing to
 *    Mercury is a separate, currently disabled step.
 */

function currentPeriod(): string {
  return new Date().toISOString().slice(0, 7);
}

function BucketBadge({ bucket }: { bucket: string }) {
  const styles: Record<string, string> = {
    owner: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
    team: 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200',
    performance: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  };
  return (
    <Badge variant="secondary" className={styles[bucket] ?? ''}>
      {bucket}
    </Badge>
  );
}

function RunCard({ run, onApproved }: { run: QueuedRun; onApproved: () => void }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const alreadyApproved = run.existingRun && run.existingRun.status !== 'draft';
  const canApprove = run.ok && !alreadyApproved;

  // An approved run displays what was RECORDED. `run.lines` is a fresh
  // recomputation from current data — useful as a preview, dishonest as a
  // receipt. If the inputs have since moved, `recorded.drift` says so rather
  // than quietly redrawing history.
  const displayLines = run.recorded?.lines ?? run.lines;
  const displayOwnerEach = run.recorded?.totals?.ownerEachCents ?? run.totals?.ownerEachCents ?? null;

  const grouped = useMemo(() => {
    const by: Record<string, typeof displayLines> = { team: [], performance: [], owner: [] };
    for (const l of displayLines) (by[l.bucket] ??= []).push(l);
    return by;
  }, [displayLines]);

  const approve = useCallback(async () => {
    setSubmitting(true);
    try {
      const res = await payoutsAPI.approve(run.clientId, run.period);
      toast.success(`${run.clientName} approved`, {
        description: `${res.distributions} distribution lines recorded. ${res.note}`,
      });
      setConfirmOpen(false);
      setTyped('');
      onApproved();
    } catch (e) {
      toast.error('Approval failed', { description: (e as Error).message });
    } finally {
      setSubmitting(false);
    }
  }, [run.clientId, run.clientName, run.period, onApproved]);

  return (
    <Card className={run.ok ? '' : 'border-destructive/40'}>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              {run.clientName}
              {alreadyApproved && (
                <Badge variant="outline" className="gap-1">
                  <CheckCircle2 className="h-3 w-3" /> {run.existingRun!.status}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>{run.period}</CardDescription>
          </div>
          {displayOwnerEach !== null && (
            <div className="text-right">
              <div className="text-2xl font-semibold tabular-nums">
                {formatCents(displayOwnerEach)}
              </div>
              <div className="text-xs text-muted-foreground">
                {run.recorded
                  ? 'per partner (as approved)'
                  : run.funded
                    ? 'per partner'
                    : 'per partner (unfunded preview)'}
              </div>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* The plain-English statement, straight from the engine. */}
        {run.narrative && <p className="text-sm leading-relaxed">{run.narrative}</p>}

        {run.attributionCaveat && !run.recorded && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Check which payments funded this</AlertTitle>
            <AlertDescription>{run.attributionCaveat}</AlertDescription>
          </Alert>
        )}

        {run.recorded?.drift && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Inputs changed since approval</AlertTitle>
            <AlertDescription>
              This run recorded {formatCents(run.recorded.drift.recordedOwnerEachCents)} per partner.
              Recomputing from today's data gives{' '}
              {formatCents(run.recorded.drift.liveOwnerEachCents)}. The figures below are what was
              approved. A receipt settling late explains this; an edited rate does not.
            </AlertDescription>
          </Alert>
        )}

        {!run.ok && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Blocked</AlertTitle>
            <AlertDescription>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                {run.blockers.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {run.warnings.length > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Worth checking</AlertTitle>
            <AlertDescription>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                {run.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {run.totals && (
          <>
            <Separator />
            {/* Received vs expected, always both. The card previously showed only
                a revenue figure derived from the contract, which read as though
                the money had arrived. */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
              <Figure label="Cash received" value={run.receivedCents} emphasis />
              {run.expectedCents > 0 && run.expectedCents !== run.receivedCents && (
                <Figure label="Expected (contract)" value={run.expectedCents} />
              )}
              <Figure label="Service revenue" value={run.totals.serviceRevenueCents} />
              <Figure label="Overhead" value={-run.totals.overheadCents} />
              <Figure label="Team comp" value={-run.totals.teamCompCents} />
              {run.totals.performanceRevenueCents > 0 && (
                <>
                  <Figure label="Performance revenue" value={run.totals.performanceRevenueCents} />
                  <Figure label="Eligible shares" value={-run.totals.eligibleSharesCents} />
                </>
              )}
              <Figure label="Owner pool" value={run.totals.ownerPoolCents} emphasis />
            </div>
            {run.totals.actualMarginPct !== null && (
              <p className="text-xs text-muted-foreground">
                Actual margin {run.totals.actualMarginPct}% — an output of the model, not an input.
              </p>
            )}
          </>
        )}

        {displayLines.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              {(['team', 'performance', 'owner'] as const).map((bucket) =>
                grouped[bucket]?.length ? (
                  <div key={bucket} className="space-y-1">
                    <BucketBadge bucket={bucket} />
                    {grouped[bucket].map((l, i) => (
                      <div
                        key={`${l.memberId}-${bucket}-${i}`}
                        className="flex items-baseline justify-between gap-4 text-sm"
                      >
                        <span>{l.name}</span>
                        <span className="tabular-nums">{formatCents(l.amountCents)}</span>
                      </div>
                    ))}
                  </div>
                ) : null,
              )}
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          {alreadyApproved ? (
            <span className="text-sm text-muted-foreground">
              Approved{run.existingRun?.approved_at ? ` ${new Date(run.existingRun.approved_at).toLocaleDateString()}` : ''}
            </span>
          ) : (
            <Button disabled={!canApprove} onClick={() => setConfirmOpen(true)}>
              {canApprove ? 'Pay' : 'Blocked'}
            </Button>
          )}
        </div>
      </CardContent>

      <Dialog
        open={confirmOpen}
        // Clearing on every close matters: the text survived a Cancel, so
        // reopening the dialog presented an already-armed Confirm button.
        onOpenChange={(open) => {
          setConfirmOpen(open);
          if (!open) setTyped('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve {run.clientName} — {run.period}</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2 text-sm">
                <p>{run.narrative}</p>
                <p className="rounded-md bg-muted p-3">
                  This records the distributions as <strong>marked</strong>. It does not move money —
                  queueing to Mercury is a separate step and is currently disabled.
                </p>
                <p>
                  Type <code className="font-mono font-semibold">APPROVE</code> to confirm.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="APPROVE"
            autoComplete="off"
            aria-label="Type APPROVE to confirm"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={approve} disabled={typed !== 'APPROVE' || submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Figure({ label, value, emphasis }: { label: string; value: number; emphasis?: boolean }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`tabular-nums ${emphasis ? 'font-semibold' : ''}`}>
        {value < 0 ? `(${formatCents(Math.abs(value))})` : formatCents(value)}
      </div>
    </div>
  );
}

export function PayoutQueuePage() {
  const [period, setPeriod] = useState(currentPeriod());
  const [data, setData] = useState<PayoutQueue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await payoutsAPI.queue(period));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-auto p-4 lg:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Payment Queue</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Every figure is computed on the server from stored terms and re-derived when you approve.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label htmlFor="period" className="text-xs text-muted-foreground">
              Period
            </label>
            <Input
              id="period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="YYYY-MM"
              className="w-32"
            />
          </div>
          <Button variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Could not load the queue</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {data && data.runs.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Lock className="h-8 w-8 text-muted-foreground" />
            <div>
              <p className="font-medium">No clients are in the payout pipeline yet</p>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">
                Clients are enabled one at a time at go-live. A disabled client is absent here
                entirely — it accrues nothing and cannot be paid.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {data?.runs.map((run) => (
          <RunCard key={`${run.clientId}:${run.period}`} run={run} onApproved={() => void load()} />
        ))}
      </div>
    </div>
  );
}

export default PayoutQueuePage;
